import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import AppLayout from '@/components/layout/AppLayout';
import { CreditCard, Spinner, Crown, Receipt, Lightning } from '@phosphor-icons/react';

export default function BillingPage() {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [b, a] = await Promise.allSettled([
          api.get('/users/me/billing'),
          api.get('/users/me/ai-usage'),
        ]);
        if (b.status === 'fulfilled') setBilling(b.value.data);
        if (a.status === 'fulfilled') setAiUsage(a.value.data);
      } catch (err) {
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <AppLayout><div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-[#002FA7]" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8" data-testid="billing-page">
        <h1 className="font-outfit text-3xl font-bold text-[#09090B] mb-8">Billing & Usage</h1>

        {billing?.is_free && (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-6" data-testid="free-account-badge">
            <Crown size={24} className="text-green-700" />
            <div>
              <p className="font-ibm font-semibold text-green-700">Free Account</p>
              <p className="text-sm text-green-600 font-ibm">Your account has unlimited free access. No charges will apply.</p>
            </div>
          </div>
        )}

        {/* Usage Summary */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-6">
          <h2 className="font-outfit text-xl font-semibold text-[#09090B] mb-4 flex items-center gap-2"><Receipt size={20} /> Usage Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Total Cases</p>
              <p className="font-outfit text-2xl font-bold text-[#09090B]">{billing?.total_cases || 0}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Free Trial Used</p>
              <p className="font-outfit text-2xl font-bold text-[#09090B]">{billing?.trial_cases_used || 0} / {billing?.trial_cases_limit || 0}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Billable Cases</p>
              <p className="font-outfit text-2xl font-bold text-[#09090B]">{billing?.billable_cases || 0}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Monthly Cost</p>
              <p className="font-outfit text-2xl font-bold text-[#002FA7]">${billing?.monthly_cost || '0.00'}</p>
            </div>
          </div>
        </div>

        {/* AI Credits Usage */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-6" data-testid="ai-credits-card">
          <h2 className="font-outfit text-xl font-semibold text-[#09090B] mb-4 flex items-center gap-2"><Lightning size={20} className="text-[#002FA7]" weight="fill" /> AI Credits Consumed</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Total Cost (USD)</p>
              <p className="font-outfit text-2xl font-bold text-[#002FA7]" data-testid="ai-cost-total">${Number(aiUsage?.cost_usd || 0).toFixed(4)}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Total Tokens</p>
              <p className="font-outfit text-2xl font-bold text-[#09090B]">{(aiUsage?.total_tokens || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Input Tokens</p>
              <p className="font-outfit text-2xl font-bold text-[#09090B]">{(aiUsage?.input_tokens || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">Output Tokens</p>
              <p className="font-outfit text-2xl font-bold text-[#09090B]">{(aiUsage?.output_tokens || 0).toLocaleString()}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-[#6B7280] font-ibm">Model: Claude Sonnet 4.5 · Pricing: $3 / 1M input tokens, $15 / 1M output tokens.</p>
        </div>

        {/* Pricing Info */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h2 className="font-outfit text-xl font-semibold text-[#09090B] mb-4 flex items-center gap-2"><CreditCard size={20} /> Pricing</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
              <div>
                <p className="font-ibm font-semibold text-[#09090B]">Per Case / Month</p>
                <p className="text-sm text-[#6B7280] font-ibm">Each active investigation case</p>
              </div>
              <p className="font-outfit text-2xl font-bold text-[#002FA7]">${billing?.billing?.price_per_case || '9.99'}<span className="text-sm text-[#6B7280] font-ibm">/mo</span></p>
            </div>
            <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
              <div>
                <p className="font-ibm font-semibold text-[#09090B]">Free Trial</p>
                <p className="text-sm text-[#6B7280] font-ibm">Create your first case(s) for free</p>
              </div>
              <p className="font-outfit text-2xl font-bold text-[#34C759]">{billing?.billing?.trial_cases || 1} case{(billing?.billing?.trial_cases || 1) > 1 ? 's' : ''}</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-[#6B7280] font-ibm">Pricing is set by the platform administrator. Contact support for enterprise pricing or questions.</p>
        </div>
      </div>
    </AppLayout>
  );
}
